/**
 * Copyright 2023 AutoZone, Inc.
 * Content is confidential to and proprietary information of AutoZone, Inc., its
 * subsidiaries and affiliates.
 */
import { getAxios } from '@/lib/axios';
import { type AxiosInstance, type AxiosResponse } from 'axios';
import { createQuery, type CustomUseQueryOptions } from '@/utils/createReactQuery';
import { requestBaseURL } from '@/config/serviceAPI';
import type { OrderHistoryResponse, OrderStatus } from '../interface';
import { useAppData } from '@/hooks/useAppData';

const URL = `${requestBaseURL}/ecomm/b2c/v3/currentUser/orders`;

type OrderHistoryOptions = {
  fromDate?: string;
  toDate?: string;
  limit?: number;
  orderStatus: OrderStatus | null;
};

type OrderHistoryData = OrderHistoryResponse;

const getOrderHistory = async (options: OrderHistoryOptions, axiosInstance?: AxiosInstance) => {
  const { fromDate, toDate, orderStatus, limit } = options;

  const params = {
    fromDate: fromDate ?? undefined,
    toDate: toDate ?? undefined,
    orderStatus: orderStatus ?? undefined,
    limit,
  };

  const response = await getAxios(axiosInstance).get<OrderHistoryResponse>(URL, {
    params,
  });

  return selector(response);
};

export const selector = (response: AxiosResponse<OrderHistoryResponse>): OrderHistoryData =>
  response.data;

const {
  useData: useOrderHistoryData,
  prefetch: prefetchOrdersHistory,
  query: orderHistoryQuery,
} = createQuery<OrderHistoryData, OrderHistoryOptions>('orderHistory', getOrderHistory);

type OrderHistoryQueryOptions<SelectData> = OrderHistoryOptions &
  CustomUseQueryOptions<OrderHistoryData, SelectData> & { enabled?: boolean };

const useOrderHistory = <SelectData = OrderHistoryData>({
  orderStatus,
  ...options
}: OrderHistoryQueryOptions<SelectData>) => {
  const { appData } = useAppData();
  const { userAuthenticated } = appData;

  const { enabled = true } = options;

  return useOrderHistoryData<SelectData>({
    ...options,
    orderStatus: orderStatus || null,
    enabled: Boolean(userAuthenticated) && enabled,
  });
};

export { useOrderHistory, prefetchOrdersHistory, orderHistoryQuery };

export type { OrderHistoryData };
